import {
   Box,
   Button,
   ButtonProps,
   CardActionArea,
   Checkbox,
   createTheme,
   darken,
   Dialog,
   OutlinedInput,
   ScopedCssBaseline,
   styled, Tab, Tabs, TabsProps,
   TextField,
   ThemeProvider,
   Typography
} from '@mui/material';
import {atom, selector, selectorFamily, useRecoilState, useRecoilValue} from "recoil";
import React, {FC, useEffect, useState} from "react";
import {TradeInLayout, TradeInLayoutH1, TradeInLayoutMainPanel, useLayoutProviderBrandLayoutQuery} from "../../Queries";
import {AccessContextData} from "../access/AppContextProvider";
import {merge} from "lodash";
import {TypographyProps} from "@mui/material/Typography/Typography";
import {defaultLayout} from "./LayoutDefaults";
import {BoxProps} from "@mui/material/Box/Box";
import {CardActionAreaProps} from "@mui/material/CardActionArea/CardActionArea";
import {GraphQL} from "../../utility/GraphQL";
import {Progress} from "../../components/Progress";
import {TabProps} from "@mui/material/Tab/Tab";

export const LayoutAtom = atom<TradeInLayout>({
   key: 'LayoutData',
   default: defaultLayout
});

export const PanelLayout = selectorFamily<TradeInLayoutMainPanel, 'main' | 'contrast' | 'front'>({
   key: 'LayoutType',
   get: (type) => ({get}) => {
      const tradeInLayout = get(LayoutAtom);
      if (type === 'main') {
         return tradeInLayout.mainPanel as TradeInLayoutMainPanel;
      } else if (type === 'contrast') {
         return tradeInLayout.contrastPanel as TradeInLayoutMainPanel;
      } else if (type === 'front') {
         return tradeInLayout.frontPanel as TradeInLayoutMainPanel;
      } else {
         throw new Error('Unsupported layout type ' + type);
      }
   }
})


export const GlobalLayout = selector<TradeInLayout>({
   key: 'LayoutType',
   get: ({get}) => {
      return get(LayoutAtom);
   }
})


export const LayoutProvider: FC<any> = ({children}) => {
   const appContext = useRecoilValue(AccessContextData);
   const [layout, setLayout] = useRecoilState(LayoutAtom);
   const [initialized, setInitialized] = useState<boolean>(false);

   const {data} = useLayoutProviderBrandLayoutQuery({
      variables: {
         shopId: appContext.shopId
      }
   });

   useEffect(() => {
      if (data) {
         let cleaned = clean(data.BrandLayout);
         let result = merge({}, layout, cleaned);
         setLayout(result);
         setInitialized(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data]);

   if (!initialized) {
      return <Progress/>
   }

   return <ThemePanel>
      {children}
   </ThemePanel>
}

const ThemePanel: FC<any> = ({children}) => {
   const layout = useRecoilValue(LayoutAtom);

   const theme = createTheme({
      typography: {
         fontFamily: layout.fontFamily || undefined,
         allVariants: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale', // For Firefox on macOS
         },
      },
      components: {
         MuiOutlinedInput: {
            styleOverrides: {
               input: {
                  boxSizing: "content-box !important" as any
               }
            }
         },
         MuiCard: {
            styleOverrides: {
               root: {
                  backgroundColor: 'inherit'
               }
            }
         },
         MuiPaper: {
            styleOverrides: {
               root: {
                  borderRadius: '0px'
               }
            }
         },
         MuiDialogTitle: {
            styleOverrides: {
               root: {
                  WebkitFontSmoothing: 'antialiased',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%'
               }
            }
         }
      }
   })

   return <ThemeProvider theme={theme}>
      <ScopedCssBaseline/>
      {children}
   </ThemeProvider>
}

type DivProps = BoxProps & {
   type?: 'main' | 'contrast' | 'front'
};

export const StyledDiv = styled(Box)<DivProps>(({theme, type}) => {
   const value = useRecoilValue(LayoutAtom)
   const layout = type === 'contrast' ? value.contrastPanel :
      type === 'main' ? value.mainPanel :
         type === 'front' ? value.frontPanel : {
            textColor: 'inherit',
            backgroundColor: 'inherit'
         }

   return {
      backgroundColor: layout?.backgroundColor || undefined,
      color: layout?.textColor || undefined,
      fontFamily: value.fontFamily || undefined,
   }
});

type TypographyExtraProps = {
   type?: 'main' | 'contrast' | 'front',
}
export type StyledTypographyProps = TypographyProps & TypographyExtraProps

export const StyledTypography = styled(Typography)<StyledTypographyProps>(({theme, type, variant}) => {
   const value = useRecoilValue(LayoutAtom)

   const layout = type === 'contrast' ? value.contrastPanel :
      type === 'main' ? value.mainPanel :
         type === 'front' ? value.frontPanel : {
            textColor: 'inherit'
         }

   let textStyle: TradeInLayoutH1;
   if (variant === 'h1') {
      textStyle = value.h1 as TradeInLayoutH1;
   } else if (variant === 'h2') {
      textStyle = value.h2 as TradeInLayoutH1;
   } else if (variant === 'body1') {
      textStyle = value.text as TradeInLayoutH1;
   } else if (variant === 'subtitle1') {
      textStyle = value.subtext as TradeInLayoutH1;
   } else {
      throw new Error(variant + ' is not supported')
   }

   return {
      color: layout?.textColor || textStyle?.textColor || undefined,
      fontFamily: textStyle?.fontFamily || value.fontFamily || undefined,
      fontWeight: textStyle?.fontWeight || undefined,
      fontSize: textStyle?.fontSize ? textStyle?.fontSize + ' !important' : undefined,
      textTransform: textStyle?.uppercase ? 'uppercase' : undefined
   }
});

type StyledButtonProps = {
   styletype: 'main' | 'contrast' | 'front'
   invert?: boolean
}

export const StyledButton = styled(Button)<StyledButtonProps>(({theme, invert, variant, styletype}) => {
   const panelLayout = useRecoilValue(PanelLayout(styletype));
   const globalLayout = useRecoilValue(GlobalLayout);

   const button = panelLayout?.mainButton;

   if (variant === 'text') {
      return {
         color: globalLayout?.text?.textColor || '#000000',
         '&:hover': {
            border: button?.border || undefined
         },
         '&:disabled': {
            color: button?.disabledTextColor || darken(button?.textColor || '#000000', 0.5) + ' !important',
            backgroundColor: button?.disabledButtonColor || '#FFFFFF',
         },
         fontSize: button?.fontSize || globalLayout.text?.fontSize || undefined,
         textTransform: 'none',
         borderRadius: '0',
         fontWeight: button?.fontWeight || globalLayout.text?.fontWeight || 'bold',
         fontFamily: button?.fontFamily || globalLayout.text?.fontFamily || undefined,
         border: button?.border || undefined,
      }
   } else {
      let buttonColor = button?.textColor || undefined;
      let backgroundColor = button?.buttonColor || undefined;
      let disabledColor = button?.disabledTextColor || undefined
      let disabledBackgroundColor = button?.disabledButtonColor || undefined
      let hoverbuttonColor = button?.hoverTextColor || buttonColor;
      let hoverBackgroundColor = button?.hoverButtonColor || backgroundColor;

      if (invert) {
         buttonColor = backgroundColor
         backgroundColor = button?.textColor || undefined;
      }
      return {
         color: buttonColor,
         backgroundColor: backgroundColor + '!important',
         '&:hover': {
            color: hoverbuttonColor,
            backgroundColor: hoverBackgroundColor + '!important'
         },
         '&:disabled': {
            color: disabledColor + ' !important',
            backgroundColor: disabledBackgroundColor + ' !important',
         },
         borderRadius: '0',
         fontSize: (button?.fontSize || globalLayout.text?.fontSize || '14px') + ' !important',
         textTransform: 'none',
         fontWeight: button?.fontWeight || globalLayout.text?.fontWeight || 'bold',
         fontFamily: button?.fontFamily || globalLayout.text?.fontFamily || undefined,
         border: button?.border || undefined
      }
   }
});


type InputProps = {}

export const StyledOutlinedInput = styled(OutlinedInput)<InputProps>(() => {
   const globalLayout = useRecoilValue(GlobalLayout);

   return {
      fontFamily: globalLayout.fontFamily || undefined,
      fontSize: globalLayout.text?.fontSize || undefined,
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
         border: "1px solid #484850",
      },
      borderRadius: '0',
      "&:input": {
         marginBottom: '0px !important'
      }
   }
});

export const StyledTextInput = styled(TextField)<InputProps>(() => {
   const globalLayout = useRecoilValue(GlobalLayout);

   return {
      fontFamily: globalLayout.fontFamily || undefined,
      fontSize: globalLayout.text?.fontSize || undefined,
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
         border: `1px solid ${globalLayout.text?.textColor || '#484850'}`,
      },
      "& .MuiOutlinedInput-root": {  // Add this block
         "&.Mui-focused fieldset": {
            borderColor: globalLayout.text?.textColor || '#484850'
         }
      },
      "& .MuiFormLabel-root.Mui-focused": {
         color: globalLayout.text?.textColor || '#484850'
      },
      input: {
         marginBottom: '0 !important',
         height: 'auto !important',
         marginLeft: '0 !important',
         textIndent: '0 !important'
      },
   }
});


type CheckboxProps = {}

export const StyledCheckbox = styled(Checkbox)<CheckboxProps>(() => {
   const globalLayout = useRecoilValue(GlobalLayout);

   return {
      fontFamily: globalLayout.fontFamily || undefined,
      fontSize: globalLayout.text?.fontSize || '16px',
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
         border: "1px solid #484850",
      },
      "&.Mui-checked": {
         color: globalLayout.text?.textColor || 'black',
      },
      "& svg": {
         fontSize: '1.5rem !important',
      },
   }
});

const clean = (obj: any): any => {
   if (!obj) {
      return obj;
   }
   let result: any = {};
   Object.keys(obj).forEach(key => {
      if (obj[key] && typeof obj[key] === 'object') {
         // eslint-disable-next-line @typescript-eslint/no-unused-vars
         const cleaned = clean(obj[key]);
         if (Object.keys(cleaned).length) {
            result[key] = cleaned;
         }
      } else if (obj[key] !== '' && obj[key] !== undefined && obj[key] !== null) {
         result[key] = obj[key];
      }
   })
   return result;
}

type DialogProps = {
   backgroundImage: string
};

export const ImageDialog = styled(Dialog)<DialogProps>(({backgroundImage}) => {
   return {
      "& .MuiDialog-paper": {
         backgroundImage: 'url(' + backgroundImage + ')',
         backgroundRepeat: "no-repeat",
         backgroundSize: "contain",
         backgroundPosition: "center",
         overflowX: 'hidden',
         overflowY: 'hidden',
      },
      "& .MuiDialogContent-root": {
         padding: 0
      }
   }
});

type AddItemButtonProps = {
   isEmpty: boolean
}

export const AddItemButton = styled(Button, {
   shouldForwardProp: (prop) => prop !== 'isEmpty'
})<ButtonProps & AddItemButtonProps>(({isEmpty}) => {
   const globalLayout = useRecoilValue(GlobalLayout);

   let emptyButton = globalLayout.widgets?.addItemButtonBlank;
   let normalButton = globalLayout.widgets?.addItemButton;

   if (!emptyButton || GraphQL.isEmpty(emptyButton)) {
      emptyButton = normalButton
   }

   let addItemButtonStyle = isEmpty ? emptyButton : normalButton;

   let buttonColor = addItemButtonStyle?.textColor || '#000000';
   let backgroundColor = addItemButtonStyle?.buttonColor || 'inherit';
   let disabledColor = addItemButtonStyle?.disabledTextColor || '#ffffff'
   let disabledBackgroundColor = addItemButtonStyle?.disabledButtonColor || undefined
   let hoverbuttonColor = addItemButtonStyle?.hoverTextColor || buttonColor;
   let hoverBackgroundColor = addItemButtonStyle?.hoverButtonColor || backgroundColor;
   let buttonBorder = addItemButtonStyle?.border || '1px dashed #FF5376'

   return {
      color: buttonColor + ' !important',
      backgroundColor: backgroundColor + ' !important',
      border: buttonBorder + ' !important',
      '&:hover': {
         color: hoverbuttonColor + ' !important',
         backgroundColor: hoverBackgroundColor + ' !important',
         border: buttonBorder + ' !important'
      },
      '&:disabled': {
         color: disabledColor + ' !important',
         backgroundColor: disabledBackgroundColor + ' !important',
         border: '1px solid ' + disabledColor + ' !important'
      },
      fontSize: (addItemButtonStyle?.fontSize || globalLayout.text?.fontSize || '12px') + ' !important',
      fontWeight: addItemButtonStyle?.fontWeight || globalLayout.text?.fontWeight || 'bold',
      fontFamily: addItemButtonStyle?.fontFamily || globalLayout.text?.fontFamily || undefined,
      paddingLeft: '36px',
      paddingRight: '36px',
      paddingTop: '10px',
      paddingBottom: '10px',
      lineHeight: '1.5em'
   }
});


type CartContinueProps = ButtonProps & {
   nextEnabled: boolean
}

export const CartContinueButton = styled(Button, {
   shouldForwardProp: (prop) => prop !== 'nextEnabled'
})<CartContinueProps>(({nextEnabled}) => {
   const globalLayout = useRecoilValue(GlobalLayout);

   let button = globalLayout.widgets?.continueButton;

   let disabledColor = button?.disabledTextColor || button?.textColor || '#C0C0C0'
   let disabledBackgroundColor = button?.disabledButtonColor || button?.buttonColor || undefined

   let _buttonColor = button?.textColor || '#000000';
   let _backgroundColor = button?.buttonColor || 'inherit';
   let buttonColor = nextEnabled ? _buttonColor : disabledColor;
   let backgroundColor = nextEnabled ? _backgroundColor : disabledBackgroundColor;

   let hoverbuttonColor = nextEnabled ? button?.hoverTextColor || _buttonColor : undefined;
   let hoverBackgroundColor = nextEnabled ? button?.hoverButtonColor || _backgroundColor : undefined;

   let buttonBorder = button?.border || '1px solid transparent'

   return {
      color: buttonColor + '!important',
      backgroundColor: backgroundColor + '!important',
      border: buttonBorder + '!important',
      '&:hover': {
         color: hoverbuttonColor + '!important',
         backgroundColor: hoverBackgroundColor + '!important'
      },
      '&:disabled': {
         color: buttonColor + '!important',
         backgroundColor: backgroundColor + '!important'
      },
      borderRadius: '0',
      fontSize: button?.fontSize || globalLayout.text?.fontSize || '12px',
      fontWeight: button?.fontWeight || globalLayout.text?.fontWeight || 'bold',
      fontFamily: button?.fontFamily || globalLayout.text?.fontFamily || undefined,
      paddingLeft: '36px',
      paddingRight: '36px',
      paddingTop: '15px',
      paddingBottom: '15px',
      lineHeight: '12px',
      whiteSpace: 'nowrap',
      minWidth: '236px'
   }
});


type CartPreviousProps = ButtonProps & {
   visible: boolean
}

export const CartPreviousButton = styled(Button, {
   shouldForwardProp: (prop) => prop !== 'visible'
})<CartPreviousProps>(({visible}) => {
   const globalLayout = useRecoilValue(GlobalLayout);

   const value = useRecoilValue(LayoutAtom)

   const layout = value.mainPanel

   const textStyle = value.text;

   return {
      color: layout?.textColor || textStyle?.textColor || undefined,
      backgroundColor: globalLayout.widgets?.cartBackgroundColor || undefined,
      '&:hover': {
         color: textStyle?.textColor || '#000000',
         border: '1px solid transparent'
      },
      borderRadius: '0',
      fontSize: textStyle?.fontSize || undefined,
      fontWeight: textStyle?.fontWeight || undefined,
      fontFamily: textStyle?.fontFamily || value.fontFamily || undefined,
      border: '1px solid transparent',
      paddingLeft: '10px',
      paddingRight: '10px',
      paddingTop: '10px',
      paddingBottom: '10px',
      minWidth: '20px',
      display: visible ? 'flex' : 'none',
   }
});


type UploadImageBoxProps = BoxProps & {
   disabled: boolean
}

export const UploadImageBox = styled(Box)<UploadImageBoxProps>(({theme, disabled}) => {
   const globalLayout = useRecoilValue(LayoutAtom)

   const uploadPhoto = globalLayout.widgets?.uploadPhoto;

   let color = uploadPhoto?.color || '#000000';
   let borderStyle = uploadPhoto?.border || '2px dashed #FF5376'
   if (disabled) {
      color = uploadPhoto?.disabledColor || '#e0e0e0';
      borderStyle = uploadPhoto?.disabledBorder || '2px dashed #e0e0e0';
   }

   return {
      color: color,
      backgroundColor: 'transparent',
      fontSize: globalLayout.text?.fontSize || '12px',
      fontWeight: globalLayout.text?.fontWeight || 'bold',
      fontFamily: globalLayout.text?.fontFamily || undefined,
      border: borderStyle
   }
});

// close dialog icon


type CardActionAreaExtraProps = {}

export const StyledCardActionArea = styled(CardActionArea)<CardActionAreaProps & CardActionAreaExtraProps>(() => {
   const globalLayout = useRecoilValue(LayoutAtom)

   return {
      color: (globalLayout.mainPanel?.textColor || '#000000') + ' !important',
      backgroundColor: (globalLayout.mainPanel?.backgroundColor || '#FFFFFF') + ' !important',
      ".MuiCardActionArea-focusHighlight": {
         backgroundColor: "#606060 !important"
      }
   }
});

export const StyledInputLabel = styled(StyledTypography)(() => {
   return {
      color: '#999999',
      fontSize: '95%',
      fontWeight: 400
   }
})


// Tabs

type ExtraTabsProps = {}

export const StyledTabs = styled(Tabs)<TabsProps & ExtraTabsProps>(() => {
   return {
      color: '#000000 !important',
      backgroundColor: '#F8F8F8 !important',
      '& .MuiTabs-indicator': {
         backgroundColor: '#808080'
      },
   }
});

type ExtraTabProps = {}

export const StyledTab = styled(Tab)<TabProps & ExtraTabProps>(() => {
   // Style the tabs text color to black
   return {
      color: '#000000 !important',
      backgroundColor: '#F4F4F4 !important',
      ".MuiCardActionArea-focusHighlight": {
         backgroundColor: "#606060 !important"
      },
      fontWeight: 'bold'
   }
});
